import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text } from 'react-native';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

type VerifiedKind = 'identity' | 'phone';

/**
 * Σήμα επαλήθευσης. `kind="identity"` (προεπιλογή) = χρυσό ✓, το ανώτερο
 * επίπεδο (ταυτότητα). `kind="phone"` = πράσινο ✓, μόνο επαληθευμένο κινητό.
 * `compact` δείχνει μόνο το εικονίδιο (π.χ. δίπλα σε όνομα σε λίστα), αλλιώς
 * εικονίδιο + κείμενο. Με tap εμφανίζει επεξήγηση.
 */
export function VerifiedBadge({ compact = false, kind = 'identity' }: { compact?: boolean; kind?: VerifiedKind }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const color = kind === 'phone' ? colors.success : colors.primaryLight;

  function explain() {
    appAlert(
      t(kind === 'phone' ? 'verifiedBadge.alert_title_phone' : 'verifiedBadge.alert_title'),
      t(kind === 'phone' ? 'verifiedBadge.alert_body_phone' : 'verifiedBadge.alert_body'),
    );
  }

  if (compact) {
    return (
      <Pressable onPress={explain} hitSlop={8} accessibilityRole="button" accessibilityLabel={t(kind === 'phone' ? 'verifiedBadge.alert_title_phone' : 'verifiedBadge.alert_title')}>
        <Ionicons name="checkmark-circle" size={16} color={color} />
      </Pressable>
    );
  }
  return (
    <Pressable style={styles.badge} onPress={explain} accessibilityRole="button">
      <Ionicons name="checkmark-circle" size={14} color={color} />
      <Text style={[styles.text, { color }]}>{kind === 'phone' ? t('profile.phone_verified') : t('common.verified')}</Text>
    </Pressable>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    badge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      alignSelf: 'flex-start',
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
    },
    text: {
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
  });
}
